/**
 * Billing plans + Stripe configuration.
 *
 * Pricing is CONFIRMED (Task 18 handoff): Single $14.99/mo (1 creator),
 * Family $24.99/mo (up to 3 kids). The earlier $9.99 launch-spec figure is
 * superseded.
 *
 * Copy rule (CI guard ``test_property_no_unlimited_copy``): the banned
 * metered-usage superlative must never appear in any billing-facing string.
 * The plan blurbs/features below are written accordingly.
 *
 * The Stripe price IDs are created out-of-process (Stripe Dashboard) and wired
 * in via env at build time. We never invent fake ``price_…`` IDs in code — the
 * placeholders below resolve to ``null`` when the env var is absent, and the
 * checkout flow simply passes ``plan_id`` to the backend, which owns the
 * server-side price-ID mapping. The exported constants exist so the web build
 * can also hand a price ID directly to Stripe if/when that path is enabled.
 */

import type { PlanId } from "./types";

export interface BillingPlan {
  id: PlanId;
  name: string;
  /** Price in cents (display only — Stripe is authoritative). */
  price: number;
  /** Number of kid seats this plan covers. */
  seats: number;
  /** Recommended plan (shows the POPULAR ribbon). */
  rec?: boolean;
  blurb: string;
  per: string;
  features: string[];
}

export const WBM_PLANS: BillingPlan[] = [
  {
    id: "single",
    name: "Single",
    price: 1499,
    seats: 1,
    blurb: "One young creator",
    per: "1 kid profile",
    features: [
      "One kid profile",
      "Create & save your worlds",
      "Avatar Studio",
      "Parental controls & safety",
    ],
  },
  {
    id: "family",
    name: "Family",
    price: 2499,
    seats: 3,
    rec: true,
    blurb: "The whole household",
    per: "up to 3 kids",
    features: [
      "Up to 3 kid profiles",
      "Everything in Single",
      "Build-together time",
      "One simple bill",
    ],
  },
];

export function planById(id: string | null | undefined): BillingPlan {
  return WBM_PLANS.find((p) => p.id === id) ?? WBM_PLANS[0];
}

/** Format cents as a friendly price (drops the cents when whole). */
export function money(cents: number): string {
  const v = cents / 100;
  return "$" + (Number.isInteger(v) ? String(v) : v.toFixed(2));
}

/**
 * Stripe configuration, sourced from build-time env (Vite). All optional —
 * the UI degrades gracefully (the Stripe card field shows a configuration
 * notice) when the publishable key is absent, e.g. in unit tests.
 */
interface ImportMetaEnvLike {
  VITE_STRIPE_PUBLISHABLE_KEY?: string;
  VITE_STRIPE_PRICE_SINGLE?: string;
  VITE_STRIPE_PRICE_FAMILY?: string;
}

const _env: ImportMetaEnvLike =
  (import.meta as unknown as { env?: ImportMetaEnvLike }).env ?? {};

export const STRIPE_PUBLISHABLE_KEY: string | null =
  _env.VITE_STRIPE_PUBLISHABLE_KEY ?? null;

/** Per-plan Stripe price IDs (created out-of-process; null until configured). */
export const STRIPE_PRICE_IDS: Record<PlanId, string | null> = {
  single: _env.VITE_STRIPE_PRICE_SINGLE ?? null,
  family: _env.VITE_STRIPE_PRICE_FAMILY ?? null,
};

/** Default trial length, mirrored from /config/public when available. */
export const DEFAULT_TRIAL_DAYS = 7;
